<template>
  <div class="selection-section">
    <!-- 顶部状态栏 -->
    <div class="selection-header">
      <div class="current-team-info">
        <div class="team-avatar">
          <a-icon type="team" />
        </div>
        <div class="team-details">
          <h2 v-if="currentTeam" class="team-name">{{ currentTeam.name }} 团队选择中</h2>
        </div>
      </div>

      <div class="countdown-area">
        <div class="countdown-info">
          <span class="countdown-label">剩余时间</span>
          <div class="countdown-display">
            <a-statistic-countdown :value="selectionDeadline" format="mm:ss" @finish="$emit('change')" />
          </div>
        </div>
        <div class="countdown-progress-bar">
          <div class="progress-fill" :style="{ width: countdownProgress + '%' }" />
        </div>
      </div>
    </div>

    <!-- 左右布局主体 -->
    <div class="section-layout">
      <!-- 左侧：团队面板 -->
      <div class="team-panel">
        <div class="panel-header">
          <a-icon type="team" />
          团队状态
          <span class="team-count">{{ teamList.length }}个团队</span>
        </div>

        <div class="team-list">
          <div
            v-for="team in teamsWithStatus"
            :key="team.id"
            :class="[
              'team-item',
              {
                active: team.status === 'selecting',
                completed: team.status === 'completed',
                waiting: team.status === 'waiting',
                rejected: team.status === 'rejected',
                eliminated: team.status === 'eliminated'
              }
            ]"
          >
            <div class="team-header">
              <div class="team-avatar">
                <a-icon type="team" />
              </div>
              <div class="team-info">
                <div class="team-name">{{ team.name }}</div>
                <div class="team-status">
                  <template v-if="team.status === 'completed'">
                    <span class="status-completed">
                      <a-icon type="check-circle" />
                      已选择
                    </span>
                  </template>
                  <template v-else-if="team.status === 'selecting'">
                    <span class="status-selecting">
                      <a-icon type="clock-circle" />
                      选择中
                    </span>
                  </template>
                  <template v-else-if="team.status === 'rejected'">
                    <span class="status-rejected">
                      <a-icon type="minus-circle" />
                      已放弃
                    </span>
                  </template>
                  <template v-else-if="team.status === 'eliminated'">
                    <span class="status-eliminated">
                      <a-icon type="close-circle" />
                      已出局
                    </span>
                  </template>
                  <template v-else>
                    <span class="status-waiting">
                      <a-icon type="hourglass" />
                      等待中
                    </span>
                  </template>
                </div>
              </div>
            </div>

            <!-- 已选择的需求 -->
            <div v-if="team.selectedRequirements && team.selectedRequirements.length > 0" class="team-selections">
              <div class="selection-list">
                <div v-for="(requirement, index) in team.selectedRequirements" :key="requirement.id" class="selection-card" :class="{ latest: index === team.selectedRequirements.length - 1 }">
                  <span class="requirement-tag">BR-{{ requirement.source_id }}</span>
                  <a class="requirement-title" :href="requirementLink(requirement)" target="_blank">{{ requirement.source_name || '需求标题' }}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：需求池 -->
      <div class="requirement-panel">
        <div class="panel-header">
          <a-icon type="shopping" />
          需求池
          <span class="requirement-count">{{ availableCount }}/{{ requirementPool.length }}可选</span>
        </div>

        <div class="requirement-grid">
          <div
            v-for="requirement in requirementPool"
            :key="requirement.source_id"
            :class="[
              'requirement-card',
              {
                selected: selectedRequirement?.id === requirement.id,
                taken: requirement.status === 3,
                available: requirement.status === 1,
                failed: requirement.status === 4,
                clickable: requirement.status === 1 && canCurrentUserSelect
              }
            ]"
            @click="selectRequirement(requirement)"
          >
            <div class="card-content">
              <div class="requirement-tag">BR-{{ requirement.source_id }}</div>
              <a class="requirement-title" :href="requirementLink(requirement)" target="_blank">{{ requirement.source_name || '需求标题' }}</a>

              <!-- 选择状态 -->
              <div class="requirement-status">
                <template v-if="requirement.status === 3">
                  <div class="taken-info">
                    <a-icon type="team" />
                    <span>{{ requirement.winner?.name || '已被选择' }}</span>
                  </div>
                </template>
                <template v-else-if="requirement.status === 4">
                  <div class="failed-info">
                    <a-icon type="close-circle" />
                    <span>流拍</span>
                  </div>
                </template>
                <template v-else-if="selectedRequirement?.id === requirement.id">
                  <div class="selected-info">
                    <a-icon type="check-circle" />
                    <span>已选中</span>
                  </div>
                </template>
                <template v-else-if="requirement.status === 1">
                  <div class="available-info">
                    <a-icon type="shopping-cart" />
                    <span>可选择</span>
                  </div>
                </template>
                <template v-else>
                  <div class="pending-info">
                    <a-icon type="clock-circle" />
                    <span>等待中</span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div v-if="canCurrentUserSelect" class="bottom-action-bar">
      <div class="action-container">
        <a-button class="reject-action" :disabled="confirmLoading" :loading="skipLoading" icon="close" @click="venueActionHandle('REJECT')"> 放弃机会 </a-button>
        <a-button class="accept-action" :loading="confirmLoading" :disabled="!selectedRequirement" icon="check" @click="venueActionHandle('ACCEPT')"> 确认选择 </a-button>
      </div>
    </div>
  </div>
</template>

<script>
import { venueAction } from '@/api/business/venue';
import dayjs from 'dayjs';

export default {
  name: 'SelectionSection',
  props: {
    venueInfo: {
      type: Object,
      default: () => ({})
    },
    requirementPool: {
      type: Array,
      default: () => []
    },
    teamList: {
      type: Array,
      default: () => []
    },
    pendingRecords: {
      type: Array,
      default: () => []
    }
  },
  emits: ['change'],
  data() {
    return {
      selectedRequirement: null,
      selectionTimeLimit: 3 * 60 * 1000, // 3分钟选择时间
      selectionStartTime: null,
      confirmLoading: false,
      skipLoading: false
    };
  },
  computed: {
    // 当前有权限操作的记录（从pendingRecords判断）
    currentPendingRecord() {
      return this.pendingRecords && this.pendingRecords.length > 0 ? this.pendingRecords[0] : null;
    },
    // 判断当前用户是否有选择权限
    canCurrentUserSelect() {
      // 只有在场次状态为进行中(2)且当前活跃记录的参与者是当前用户时才能选择
      if (this.venueInfo.status !== 2) return false;
      if (!this.currentActiveRecord) return false;

      // 需要根据实际的用户身份判断逻辑来确定当前用户是否是currentActiveRecord.participant_id对应的团队成员
      // 这里暂时使用currentPendingRecord来判断（需要根据实际业务逻辑调整）
      return this.currentPendingRecord !== null;
    },
    selectionDeadline() {
      if (!this.currentActiveRecord || !this.currentActiveRecord.deadline) return Date.now() + 60000;
      return dayjs(this.currentActiveRecord.deadline).valueOf();
    },
    availableCount() {
      return this.requirementPool.filter(req => req.status === 1).length;
    },
    // 获取全局记录数据（所有团队的记录，不仅仅是当前用户的）
    allRecords() {
      return this.venueInfo?.records || this.pendingRecords || [];
    },

    // 当前正在处理的记录
    currentActiveRecord() {
      const records = this.allRecords;
      if (!records?.length) return null;

      return records.find(record => record.result === 1) || records.find(record => !record.result || record.result === 0) || records[0] || null;
    },
    // 当前选择中的团队
    currentTeam() {
      const activeRecord = this.currentActiveRecord;
      if (!activeRecord) return null;

      return this.teamList.find(team => String(team.id) === String(activeRecord.participant_id)) || null;
    },
    // 基于records计算的团队状态列表
    teamsWithStatus() {
      return this.teamList.map(team => {
        const selectedRequirements = this.getTeamSelectedRequirements(team.id);
        const latestRequirement = selectedRequirements[selectedRequirements.length - 1] || null;

        return {
          ...team,
          status: this.getTeamStatus(team.id),
          selectedRequirements,
          selectedRequirement: latestRequirement,
          selectionTime: latestRequirement?.selectionTime || null
        };
      });
    },
    requirementLink() {
      return record => {
        return this.$router.resolve({
          name: 'page:business:requirement:detail',
          params: {
            id: record.source_id
          }
        }).href;
      };
    },

    // 倒计时进度百分比
    countdownProgress() {
      if (!this.currentActiveRecord || !this.currentActiveRecord.deadline) return 100;

      const currentTime = dayjs();
      const endTime = dayjs(this.currentActiveRecord.deadline);

      // 使用实际的start_time字段，如果没有则使用deadline减去默认时间
      let startTime;
      if (this.currentActiveRecord.start_time) {
        startTime = dayjs(this.currentActiveRecord.start_time);
      } else {
        // 如果没有start_time，使用默认3分钟的选择时间
        const selectionDuration = 3 * 60 * 1000; // 3分钟
        startTime = endTime.subtract(selectionDuration, 'millisecond');
      }

      const totalTime = endTime.diff(startTime);
      const remainingTime = endTime.diff(currentTime);

      if (remainingTime <= 0) return 0;
      if (totalTime <= 0) return 100;

      return Math.max(0, Math.min(100, (remainingTime / totalTime) * 100));
    }
  },
  methods: {
    // 根据团队ID获取团队状态
    getTeamStatus(teamId) {
      // 检查是否正在选择
      if (this.currentActiveRecord?.participant_id === teamId) {
        return 'selecting';
      }

      // 检查是否已选择需求
      if (this.requirementPool.some(req => req.status === 3 && req.winner?.id === teamId)) {
        return 'completed';
      }

      // 检查records中的状态
      const teamRecords = this.allRecords.filter(record => record.participant_id === teamId);
      if (teamRecords.some(record => record.result === 4)) return 'eliminated';
      if (teamRecords.some(record => record.result === 2)) return 'completed';

      return 'waiting';
    },

    // 根据团队ID获取团队选择的需求（支持多个）
    getTeamSelectedRequirements(teamId) {
      return this.requirementPool.filter(req => req.status === 3 && req.winner && req.winner.id === teamId);
    },

    selectRequirement(requirement) {
      if (this.canCurrentUserSelect && requirement.status === 1) {
        this.selectedRequirement = requirement;
      }
    },

    venueActionHandle(action) {
      const isAccept = action === 'ACCEPT';

      // 设置loading状态
      if (isAccept) {
        this.confirmLoading = true;
      } else {
        this.skipLoading = true;
      }

      // 验证操作条件
      if (isAccept && (!this.selectedRequirement || this.selectedRequirement.status !== 1)) {
        this.$message.warning('请先选择一个可用的需求');
        this.confirmLoading = false;
        return;
      }

      if (!this.currentPendingRecord) {
        this.$message.error('当前没有可操作的记录');
        this.resetLoadingState();
        return;
      }

      venueAction({
        venue_id: this.venueInfo.id,
        record_id: this.currentPendingRecord.id,
        action: action,
        product_id: isAccept ? this.selectedRequirement.id : null
      })
        .then(() => {
          const successMsg = isAccept ? `成功选择需求 BR-${this.selectedRequirement.source_id}` : '已放弃本轮选择机会';
          this.$message[isAccept ? 'success' : 'info'](successMsg);

          this.selectedRequirement = null;
          this.$emit('change');
        })
        .catch(err => {
          const actionText = isAccept ? '确认选择' : '放弃选择';
          const errorMsg = err.response?.data?.message || err.message || `${actionText}失败`;
          this.$message.error(errorMsg);
        })
        .finally(() => {
          this.resetLoadingState();
        });
    },

    resetLoadingState() {
      this.confirmLoading = false;
      this.skipLoading = false;
    },

    formatSelectionTime(timeString) {
      return timeString ? dayjs(timeString).format('HH:mm:ss') : '';
    }
  }
};
</script>

<style lang="less" scoped>
.selection-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  padding: 16px;

  .selection-header {
    padding: 16px 24px;
    background: rgba(249, 250, 251, 0.9);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    color: #1e293b;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 12px;
    margin-bottom: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 0, 0, 0.1);

    .current-team-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .team-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: linear-gradient(135deg, #4f46e5, #818cf8);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);

        .anticon {
          font-size: 20px;
          color: white;
        }
      }

      .team-details {
        .team-name {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
          color: #1e293b;
        }
      }
    }

    .countdown-area {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      min-width: 100px;

      .countdown-info {
        display: flex;
        align-items: center;

        .countdown-label {
          font-size: 12px;
          color: rgba(30, 41, 59, 0.8);
          margin-right: 4px;
        }

        .countdown-display {
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
          font-family: 'Courier New', monospace;
        }
      }

      .countdown-progress-bar {
        width: 100%;
        height: 8px;
        background: rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        overflow: hidden;

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #10b981, #f59e0b, #ef4444);
          border-radius: 2px;
          transition: width 1s ease;
        }
      }
    }
  }

  .section-layout {
    flex: 1;
    display: flex;
    height: 100%;
    gap: 16px;
    overflow: hidden;

    @media (max-width: 1024px) {
      flex-direction: column;
      gap: 12px;
      padding: 12px;
    }
  }

  .bottom-action-bar {
    position: fixed;
    bottom: 48px;
    left: 55%;
    transform: translateX(-50%);
    z-index: 50;

    .action-container {
      background: rgba(249, 250, 251, 0.95);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border-radius: 50px;
      padding: 12px 24px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
      display: flex;
      gap: 120px;
      max-width: 600px;

      .reject-action,
      .accept-action {
        border: none;
        border-radius: 25px;
        padding: 20px 24px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 120px;
        justify-content: center;

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .anticon {
          font-size: 16px;
        }
      }

      .reject-action {
        background: transparent;
        color: rgba(30, 41, 59, 0.8);

        &:hover:not(:disabled) {
          color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
        }
      }

      .accept-action {
        background: linear-gradient(135deg, #4f46e5, #818cf8);
        color: white;
        box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(79, 70, 229, 0.4);
          background: linear-gradient(135deg, #818cf8, #4f46e5);
        }

        &:disabled {
          background: rgba(0, 0, 0, 0.1);
          color: rgba(0, 0, 0, 0.4);
          box-shadow: none;
        }
      }
    }
  }
}

.team-panel {
  width: 400px;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  @media (max-width: 1024px) {
    width: 100%;
    min-height: 300px;
    max-height: 400px;
  }

  .panel-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.6);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    font-weight: 600;
    color: #1e293b;
    font-size: 16px;

    .anticon {
      color: #4f46e5;
      font-size: 18px;
    }

    .team-count {
      margin-left: auto;
      font-size: 0.75rem;
      color: rgba(30, 41, 59, 0.6);
      background: rgba(0, 0, 0, 0.05);
      padding: 0.25rem 0.75rem;
      border-radius: 999px;
    }
  }

  .team-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .team-item {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    &.active {
      border-color: rgba(79, 70, 229, 0.3);
      box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
      background: rgba(79, 70, 229, 0.1);
    }

    &.completed {
      border-color: rgba(16, 185, 129, 0.3);
      background: rgba(16, 185, 129, 0.1);
    }

    &.rejected {
      border-color: rgba(239, 68, 68, 0.3);
      background: rgba(239, 68, 68, 0.1);
    }

    &.eliminated {
      border-color: rgba(211, 47, 47, 0.3);
      background: rgba(211, 47, 47, 0.1);
      opacity: 0.7;

      .team-header,
      .team-selections {
        opacity: 0.6;
      }
    }

    .team-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;

      .team-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: rgba(79, 70, 229, 0.1);
        color: #4f46e5;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border: 1px solid rgba(79, 70, 229, 0.2);
      }

      .team-info {
        flex: 1;
        min-width: 0;

        .team-name {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .team-status {
          font-size: 0.8rem;

          .status-completed {
            color: #00b42a;
          }

          .status-selecting {
            color: #165dff;
          }

          .status-rejected {
            color: #f53f3f;
          }

          .status-eliminated {
            color: #d32f2f;
            font-weight: bold;
          }

          .status-waiting {
            color: #86909c;
          }
        }
      }
    }

    .team-selections {
      padding: 0 12px 12px;

      .selection-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-height: 200px;
        overflow-y: auto;
        border-radius: 8px;
        padding: 8px;
        background-color: rgba(0, 0, 0, 0.03);
      }

      .selection-card {
        .requirement-tag {
          font-size: 0.7rem;
          font-weight: 600;
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          padding: 0.15rem 0.5rem;
          border-radius: 999px;
          display: inline-block;
        }

        .requirement-title {
          font-size: 0.8rem;
          font-weight: 600;
          color: #1e293b;
          line-height: 1.3;
          margin-bottom: 4px;
          margin-left: 4px;
        }
      }
    }
  }
}

.requirement-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  .panel-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.6);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    font-weight: 600;
    color: #1e293b;
    font-size: 16px;

    .anticon {
      color: #4f46e5;
      font-size: 18px;
    }

    .requirement-count {
      margin-left: auto;
      font-size: 0.75rem;
      color: rgba(30, 41, 59, 0.6);
      background: rgba(0, 0, 0, 0.05);
      padding: 0.25rem 0.75rem;
      border-radius: 999px;
    }
  }

  .requirement-grid {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 18px;
    align-content: start;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 16px;
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 12px;
      padding: 12px;
    }
  }

  .requirement-card {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 12px;
    transition: all 0.3s ease;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    &.clickable {
      cursor: pointer;
      position: relative;

      &:hover {
        border-color: rgba(79, 70, 229, 0.3);
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(79, 70, 229, 0.1);
      }

      &::before {
        content: '';
        position: absolute;
        top: 8px;
        right: 8px;
        width: 20px;
        height: 20px;
        border: 2px solid rgba(0, 0, 0, 0.1);
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.8);
        transition: all 0.3s ease;
      }
    }

    &.selected {
      border-color: rgba(79, 70, 229, 0.5);
      background: rgba(79, 70, 229, 0.15);
      box-shadow: 0 8px 24px rgba(79, 70, 229, 0.2);
      transform: translateY(-2px);

      &::before {
        content: '✓';
        position: absolute;
        top: 8px;
        right: 8px;
        width: 20px;
        height: 20px;
        border: 2px solid #4f46e5;
        border-radius: 50%;
        background: #4f46e5;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: bold;
      }
    }

    &.taken {
      border-color: rgba(16, 185, 129, 0.3);
      background: rgba(16, 185, 129, 0.1);
      opacity: 0.8;
    }

    &.failed {
      border-color: rgba(239, 68, 68, 0.3);
      background: rgba(239, 68, 68, 0.1);
      opacity: 0.7;
    }

    .card-content {
      padding: 16px;

      .requirement-tag {
        font-size: 0.7rem;
        font-weight: 600;
        background: rgba(59, 130, 246, 0.1);
        color: #3b82f6;
        padding: 0.2rem 0.6rem;
        border-radius: 999px;
        display: inline-block;
        margin-bottom: 8px;
      }

      .requirement-title {
        font-size: 0.9rem;
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 6px;
        line-height: 1.4;
      }

      .requirement-status {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.8rem;
        font-weight: 500;

        .taken-info {
          color: #00b42a;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .failed-info {
          color: #f53f3f;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .selected-info {
          color: #165dff;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .available-info {
          color: #00b42a;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .pending-info {
          color: #86909c;
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }
    }
  }
}

:deep(.ant-statistic-content) {
  color: #1e293b !important;
  font-weight: 700 !important;
  font-size: 1.25rem !important;
}
</style>
